import {createMuiTheme, Theme, ThemeOptions} from '@material-ui/core';
import colors from 'shared/aptitude/colors';
import {
  FontStyle,
  FontStyleOptions,
  TypographyStyle,
  TypographyStyleOptions,
  TypographyUtils
} from '@material-ui/core/styles/createTypography';
import {Palette} from '@material-ui/core/styles/createPalette';

export type RaiseMeVariants = 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'button'
  | 'overline'
  | 'inherit'
  | 'dropcaps'
  | 'label'

export type Typography = Record<RaiseMeVariants, TypographyStyle>
  & FontStyle
  & TypographyUtils
  & {}

export type RaiseMeTheme = Theme & {
  typography: Typography;
  zIndex: {
    drawer: number;
    contentOverlay: number;
  };
  palette: {
    text: {
      highlight: string;
    };
  };
}

type RaiseMeTypographyOptions = Partial<Record<RaiseMeVariants, TypographyStyleOptions> & FontStyleOptions> & {}
export type RaiseMeThemeOptions = ThemeOptions & {
  zIndex: {
    contentOverlay: number;
  };
  typography?: RaiseMeTypographyOptions | ((palette: Palette) => RaiseMeTypographyOptions);
  palette: {
    text: {
      highlight: string;
    };
  };
}

export type FontWeight = 300 | 400 | 600 | 700;

const theme = createMuiTheme({
  overrides: {
    MuiTooltip: {
      arrow: {
        color: colors.n900_85
      },
      tooltip: {
        borderRadius: 6,
        backgroundColor: colors.n900_85,
        fontSize: '0.875rem',
        padding: '12px',
        fontWeight: 400
      }
    },
    MuiButton: {
      root: {
        textTransform: 'none'
      }
    }
  },
  breakpoints: {
    values: {
      xs: 480,
      sm: 768,
      md: 992,
      lg: 1200,
      xl: 1920
    }
  },
  palette: {
    warning: {
      main: colors.m800
    },
    primary: {
      main: colors.b500
    },
    text: {
      highlight: colors.b500,
      primary: colors.n900,
      secondary: colors.n800
    },
    grey: {
      200: colors.n200,
      400: colors.n400
    }
  },
  typography: {
    fontFamily: ['"Open Sans"', 'Arial', 'Helvetica', '"sans-serif"'].join(','),
    htmlFontSize: 16,
    fontSize: 16,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    button: {
      fontSize: '16px'
    },
    allVariants: {
      color: colors.n900
    },
    dropcaps: {
      textTransform: 'uppercase',
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.n500
    },
    // display-3
    h1: {
      fontSize: '3.5rem',
      fontWeight: 600,
      letterSpacing: '-1px',
      lineHeight: 1.2
    },
    // display-2
    h2: {
      fontSize: '2.75rem',
      fontWeight: 600,
      letterSpacing: '-1px',
      lineHeight: 1.2
    },
    // display-1
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      letterSpacing: '-1px',
      lineHeight: 1.5
    },
    // headline
    h4: {
      fontSize: '1.5rem',
      fontWeight: 400,
      lineHeight: 1.5
    },
    // title
    h5: {
      fontSize: '1.25rem',
      lineHeight: 1.5
    },
    // subheading
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5
    },
    // body
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5
    },
    // caption
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5
    },
  },
  zIndex: {
    drawer: 1002,
    contentOverlay: 500
  },
  props: {
    MuiButtonGroup: {
      disableRipple: true
    },
    MuiButton: {
      disableElevation: true,
      disableRipple: true
    },
    MuiButtonBase: {
      disableTouchRipple: true
    }
  }
});

// typography additions
theme.typography = {
  ...theme.typography,
  label: {
    ...theme.typography.body1,
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  }
};

// cards
theme.shadows[1] = '0px 3px 3px -2px rgba(0,0,0,0.02), 0px 1px 8px 0px rgba(0,0,0,0.12)';

// modals
theme.shadows[5] = '0px 3px 3px -2px rgba(0,0,0,0.02), 0px 1px 12px 2px rgba(0,0,0,0.2)';

export default theme;
